import { Transition } from '@headlessui/react'
import cx from 'classnames'
import { useMemo } from 'react'

import { Button } from '../../actions/button/Button'
import { useTranslate } from '../../hooks/useTranslate'
import { ProgressBar } from './ProgressBar'
import type { WizardStep } from './types'
import { useWizard } from './useWizard'

type Props<T extends string, Data> = {
  steps: WizardStep<T, Data>[]
  currentData: Data
  className?: string
  stepsClassName?: string
  onCompleted: () => void
}

export const Wizard = <T extends string, Data>({
  steps,
  currentData,
  className,
  stepsClassName,
  onCompleted,
}: Props<T, Data>) => {
  const translate = useTranslate()
  const [state, dispatch] = useWizard(steps)
  const indexedSteps = useMemo(
    () =>
      steps.reduce(
        (acc, step) => ({ ...acc, [step.id]: step }),
        {} as Record<T, WizardStep<T, Data>>,
      ),
    [steps],
  )

  const currentStep = indexedSteps[state.stepId]
  const isCompleted = !currentStep.next
  const isNextStepAllowedWithReason = currentStep.isNextAllowed
    ? currentStep.isNextAllowed(currentData)
    : true
  const isNextAllowed =
    typeof isNextStepAllowedWithReason === 'boolean'
      ? isNextStepAllowedWithReason
      : false
  const nextDisabledReason =
    typeof isNextStepAllowedWithReason === 'string'
      ? isNextStepAllowedWithReason
      : undefined

  const handleBackClicked = () => {
    currentStep.onBack?.(currentData)
    dispatch({ type: 'back', data: currentData })
  }

  return (
    <div className={cx('flex flex-col gap-y-2', className)}>
      <ProgressBar
        step={!currentStep.next ? steps.length : state.history.length}
        total={steps.length}
      />

      <Transition
        as="div"
        appear
        show
        enter="transition-opacity duration-500"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        className={cx('grow flex flex-col', stepsClassName)}
        key={currentStep.id}
      >
        {currentStep.render(currentData)}
      </Transition>
      <div
        className={cx(
          'flex gap-x-2',
          nextDisabledReason ? 'justify-between' : 'justify-end',
        )}
      >
        {nextDisabledReason && (
          <div className="text-error-500">{nextDisabledReason}</div>
        )}
        {!isCompleted ? (
          <div className="flex gap-x-2">
            <Button variant="discreet" onClick={handleBackClicked}>
              {translate('previous')}
            </Button>
            <Button
              variant="primary"
              onClick={() => dispatch({ type: 'next', data: currentData })}
              disabled={!isNextAllowed}
            >
              {translate('next')}
            </Button>
          </div>
        ) : (
          <Button variant="primary" onClick={onCompleted}>
            {translate('finish')}
          </Button>
        )}
      </div>
    </div>
  )
}
